import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { StarRating } from "@/components/shared/StarRating";
import { cn } from "@/lib/utils";
import {
  getPublicSurveyContext,
  submitPublicSurvey,
  type PublicSurveyContext,
} from "@/services/requestFeedbackService";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; context: PublicSurveyContext }
  | { kind: "submitted" }
  | { kind: "error"; message: string };

export function RatePage(): JSX.Element {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  // The survey email's star row links to /rate/<token>?r=N, so arriving from it
  // preselects that rating. Only the platform star — the form still needs a
  // professional rating and an NPS score, so this is a head start, not a
  // one-click submit. Out-of-range or junk values are ignored.
  const [platformRating, setPlatformRating] = useState(() => {
    const prefill = Number.parseInt(searchParams.get("r") ?? "", 10);
    return Number.isInteger(prefill) && prefill >= 1 && prefill <= 5
      ? prefill
      : 0;
  });
  const [professionalRating, setProfessionalRating] = useState(0);
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoad({ kind: "error", message: t("rate.errorNotFound") });
      return;
    }
    let active = true;
    getPublicSurveyContext(token)
      .then((context) => {
        if (!active) return;
        if (context.alreadySubmitted) {
          setLoad({ kind: "submitted" });
        } else {
          setLoad({ kind: "ready", context });
        }
      })
      .catch(() => {
        if (!active) return;
        setLoad({ kind: "error", message: t("rate.errorNotFound") });
      });
    return () => {
      active = false;
    };
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (load.kind !== "ready" || !token) return;
    if (platformRating < 1) {
      toast.error(t("rate.errorPickPlatform"));
      return;
    }
    if (load.context.hasProfessional && professionalRating < 1) {
      toast.error(t("rate.errorPickProfessional"));
      return;
    }
    if (npsScore == null) {
      toast.error(t("rate.errorPickNps"));
      return;
    }
    setSubmitting(true);
    try {
      await submitPublicSurvey(token, {
        platformRating,
        professionalRating: load.context.hasProfessional
          ? professionalRating
          : undefined,
        npsScore,
        comment: comment.trim() || undefined,
      });
      setLoad({ kind: "submitted" });
    } catch {
      toast.error(t("rate.errorSubmit"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-svh overflow-y-auto bg-primary">
      <div className="flex min-h-svh flex-col items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="space-y-6 p-6">
            {load.kind === "loading" && (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {load.kind === "error" && (
              <div className="space-y-2 text-center">
                <h1 className="text-2xl font-bold">{t("rate.errorTitle")}</h1>
                <p className="text-muted-foreground">{load.message}</p>
              </div>
            )}

            {load.kind === "submitted" && (
              <div className="space-y-2 text-center">
                <h1 className="text-2xl font-bold">{t("rate.thanksTitle")}</h1>
                <p className="text-muted-foreground">{t("rate.thanksBody")}</p>
              </div>
            )}

            {load.kind === "ready" && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold leading-tight">
                    {t("rate.title")}
                  </h1>
                  {load.context.productLabel && (
                    <p className="text-sm text-muted-foreground">
                      {t("rate.subtitle", {
                        product: load.context.productLabel,
                      })}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-base">
                    {t("rate.platformQuestion")}
                  </Label>
                  <StarRating
                    value={platformRating}
                    onChange={setPlatformRating}
                    ariaLabel={t("rate.platformQuestion")}
                  />
                </div>

                {load.context.hasProfessional && (
                  <div className="space-y-2">
                    <Label className="text-base">
                      {t("rate.professionalQuestion", {
                        name:
                          load.context.professionalName ??
                          t("rate.professionalFallback"),
                      })}
                    </Label>
                    <StarRating
                      value={professionalRating}
                      onChange={setProfessionalRating}
                      ariaLabel={t("rate.professionalQuestion", {
                        name:
                          load.context.professionalName ??
                          t("rate.professionalFallback"),
                      })}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-base">{t("rate.npsQuestion")}</Label>
                  <div
                    role="radiogroup"
                    aria-label={t("rate.npsQuestion")}
                    className="grid grid-cols-11 gap-1"
                  >
                    {Array.from({ length: 11 }, (_, i) => i).map((n) => {
                      const selected = npsScore === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          aria-label={`${n}`}
                          onClick={() => setNpsScore(n)}
                          className={cn(
                            "flex h-9 items-center justify-center rounded-md border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input bg-background hover:bg-accent hover:text-accent-foreground"
                          )}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{t("rate.npsScaleLow")}</span>
                    <span>{t("rate.npsScaleHigh")}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rate-comment" className="text-base">
                    {t("rate.commentLabel")}
                  </Label>
                  <Textarea
                    id="rate-comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t("rate.commentPlaceholder")}
                    maxLength={2000}
                    rows={4}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("rate.submit")}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
